// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Alert,
  Button,
  Center,
  Grid,
  Group,
  Loader,
  NumberInput,
  Paper,
  Select,
  Stack,
  TextInput,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DatePickerInput } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { PageHeader } from '../../components/PageHeader';
import { useEmployee, useUpdateEmployee } from '../../hooks/useEmployees';
import type { UpdateEmployeeInput } from '../../types/employee';

export default function EmployeeEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: employee, loading, error } = useEmployee(id!);
  const updateEmployee = useUpdateEmployee();

  const form = useForm<UpdateEmployeeInput>({
    initialValues: {
      firstName: '',
      lastName: '',
      middleName: '',
      email: '',
      phone: '',
      hireDate: '',
      departmentId: '',
      branchId: '',
      jobTitle: '',
      managerId: '',
      payType: 'hourly',
      payRate: 0,
      payFrequency: 'biweekly',
      filingStatus: 'single',
      allowances: 0,
      status: 'active',
    },
    validate: {
      firstName: (v) => (v && v.trim().length < 1 ? 'First name is required' : null),
      lastName: (v) => (v && v.trim().length < 1 ? 'Last name is required' : null),
      email: (v) => {
        if (!v) return null;
        if (!/^\S+@\S+\.\S+$/.test(v)) return 'Invalid email';
        return null;
      },
      payRate: (v) => (v !== undefined && v <= 0 ? 'Pay rate must be greater than 0' : null),
    },
  });

  useEffect(() => {
    if (employee) {
      form.setValues({
        firstName: employee.firstName,
        lastName: employee.lastName,
        middleName: employee.middleName || '',
        email: employee.email,
        phone: employee.phone || '',
        hireDate: employee.hireDate,
        departmentId: employee.departmentId,
        branchId: employee.branchId,
        jobTitle: employee.jobTitle,
        managerId: employee.managerId || '',
        payType: employee.payType,
        payRate: employee.payRate,
        payFrequency: employee.payFrequency,
        filingStatus: employee.filingStatus,
        allowances: employee.allowances,
        status: employee.status,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee]);

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !employee) {
    return (
      <Alert color="red" title="Error">
        Failed to load employee details.
      </Alert>
    );
  }

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      await updateEmployee.mutateAsync({ id: id!, input: values });
      notifications.show({
        title: 'Employee Updated',
        message: `${values.firstName} ${values.lastName} has been updated.`,
        color: 'green',
      });
      navigate(`/employees/${id}`);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to update employee',
        color: 'red',
      });
    }
  });

  return (
    <Stack gap="md">
      <PageHeader
        title={`Edit ${employee.firstName} ${employee.lastName}`}
        breadcrumbs={[
          { label: 'Employees', to: '/admin/workforce/employees' },
          { label: `${employee.firstName} ${employee.lastName}`, to: `/employees/${id}` },
          { label: 'Edit' },
        ]}
      />

      <Paper withBorder p="xl" radius="md">
        <form onSubmit={handleSubmit}>
          <Stack gap="lg">
            {/* Personal Info */}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput label="First Name" required {...form.getInputProps('firstName')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput label="Middle Name" {...form.getInputProps('middleName')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput label="Last Name" required {...form.getInputProps('lastName')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Email" required {...form.getInputProps('email')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Phone" {...form.getInputProps('phone')} />
              </Grid.Col>
            </Grid>

            {/* Employment */}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Status"
                  required
                  {...form.getInputProps('status')}
                  data={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                    { value: 'terminated', label: 'Terminated' },
                    { value: 'on_leave', label: 'On Leave' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <DatePickerInput
                  label="Hire Date"
                  required
                  value={form.values.hireDate ? new Date(form.values.hireDate) : null}
                  onChange={(date) =>
                    form.setFieldValue('hireDate', date ? new Date(date as string | Date).toISOString().split('T')[0] : '')
                  }
                  error={form.errors.hireDate}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Job Title" required {...form.getInputProps('jobTitle')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Department"
                  required
                  {...form.getInputProps('departmentId')}
                  data={[
                    // TODO: Populate from departments API
                    { value: 'dept-1', label: 'Engineering' },
                    { value: 'dept-2', label: 'Sales' },
                    { value: 'dept-3', label: 'Human Resources' },
                    { value: 'dept-4', label: 'Operations' },
                    { value: 'dept-5', label: 'Finance' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Branch"
                  required
                  {...form.getInputProps('branchId')}
                  data={[
                    // TODO: Populate from branches API
                    { value: 'branch-1', label: 'Headquarters' },
                    { value: 'branch-2', label: 'West Office' },
                    { value: 'branch-3', label: 'East Office' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Manager"
                  clearable
                  searchable
                  {...form.getInputProps('managerId')}
                  data={[
                    // TODO: Populate from employees API
                    { value: 'mgr-1', label: 'Sarah Johnson' },
                    { value: 'mgr-2', label: 'Mike Williams' },
                  ]}
                />
              </Grid.Col>
            </Grid>

            {/* Compensation */}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Pay Type"
                  required
                  {...form.getInputProps('payType')}
                  data={[
                    { value: 'salary', label: 'Salary' },
                    { value: 'hourly', label: 'Hourly' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label={form.values.payType === 'salary' ? 'Annual Salary' : 'Hourly Rate'}
                  required
                  prefix="$"
                  decimalScale={2}
                  thousandSeparator=","
                  min={0}
                  {...form.getInputProps('payRate')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Pay Frequency"
                  required
                  {...form.getInputProps('payFrequency')}
                  data={[
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'biweekly', label: 'Bi-Weekly' },
                    { value: 'semimonthly', label: 'Semi-Monthly' },
                    { value: 'monthly', label: 'Monthly' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Filing Status"
                  required
                  {...form.getInputProps('filingStatus')}
                  data={[
                    { value: 'single', label: 'Single' },
                    { value: 'married_joint', label: 'Married Filing Jointly' },
                    { value: 'married_separate', label: 'Married Filing Separately' },
                    { value: 'head_of_household', label: 'Head of Household' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput label="Allowances" min={0} max={10} {...form.getInputProps('allowances')} />
              </Grid.Col>
            </Grid>

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={() => navigate(`/employees/${id}`)}>
                Cancel
              </Button>
              <Button type="submit" loading={updateEmployee.isPending}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
}
